import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../../lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────
interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  status: 'draft' | 'published';
  featured_image: string;
  cover_image: string;
  tags: string[];
  reading_time: number;
  seo_title: string;
  seo_description: string;
  seo_keywords: string;
  og_image: string;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

type Tab = 'posts' | 'page-seo';

const EMPTY_FORM: Partial<BlogPost> = {
  title: '', slug: '', excerpt: '', content: '', author: 'Admin',
  status: 'draft', featured_image: '', cover_image: '', tags: [],
  reading_time: 5, seo_title: '', seo_description: '',
  seo_keywords: '', og_image: '',
};

// ─── Slug helper ─────────────────────────────────────────────────────────────
function toSlug(text: string) {
  const map: Record<string, string> = {
    ç:'c',Ç:'c',ğ:'g',Ğ:'g',ı:'i',I:'i',İ:'i',ö:'o',Ö:'o',ş:'s',Ş:'s',ü:'u',Ü:'u',
  };
  return text
    .replace(/[çÇğĞıIİöÖşŞüÜ]/g, m => map[m] || m)
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// ─── HTML Rich Text Editor ────────────────────────────────────────────────────
interface EditorProps { value: string; onChange: (html: string) => void; }

const HtmlEditor: React.FC<EditorProps> = ({ value, onChange }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isUpdating = useRef(false);
  const lastValue = useRef(value);

  useEffect(() => {
    if (editorRef.current && value !== lastValue.current && !isUpdating.current) {
      lastValue.current = value;
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const exec = (cmd: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      lastValue.current = html;
      onChange(html);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      isUpdating.current = true;
      const html = editorRef.current.innerHTML;
      lastValue.current = html;
      onChange(html);
      setTimeout(() => { isUpdating.current = false; }, 0);
    }
  };

  const insertLink = () => {
    const url = prompt('Enter link URL:', 'https://');
    if (url) exec('createLink', url);
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:', 'https://');
    if (url) exec('insertImage', url);
  };

  const setBlock = (tag: string) => {
    exec('formatBlock', tag);
  };

  const toolbarBtn = (icon: string, title: string, action: () => void, active = false) => (
    <button
      key={title}
      type="button"
      title={title}
      onMouseDown={e => { e.preventDefault(); action(); }}
      className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium transition-colors cursor-pointer
        ${active ? 'bg-[#C5A46D] text-white' : 'text-gray-700 hover:bg-gray-200'}`}
    >
      <i className={`ri-${icon}`}></i>
    </button>
  );

  const selectBlock = (
    <select
      onMouseDown={e => e.stopPropagation()}
      onChange={e => { setBlock(e.target.value); e.target.value = ''; }}
      className="h-8 text-sm border border-gray-300 rounded px-2 text-gray-700 bg-white cursor-pointer"
      defaultValue=""
    >
      <option value="" disabled>Paragraph Style</option>
      <option value="p">Paragraph</option>
      <option value="h1">Heading 1</option>
      <option value="h2">Heading 2</option>
      <option value="h3">Heading 3</option>
      <option value="h4">Heading 4</option>
      <option value="blockquote">Quote</option>
      <option value="pre">Code Block</option>
    </select>
  );

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-100 border-b border-gray-300">
        {selectBlock}
        <div className="w-px h-6 bg-gray-300 mx-1" />
        {toolbarBtn('bold', 'Bold', () => exec('bold'))}
        {toolbarBtn('italic', 'Italic', () => exec('italic'))}
        {toolbarBtn('underline', 'Underline', () => exec('underline'))}
        {toolbarBtn('strikethrough', 'Strikethrough', () => exec('strikeThrough'))}
        <div className="w-px h-6 bg-gray-300 mx-1" />
        {toolbarBtn('list-unordered', 'Bullet List', () => exec('insertUnorderedList'))}
        {toolbarBtn('list-ordered', 'Numbered List', () => exec('insertOrderedList'))}
        <div className="w-px h-6 bg-gray-300 mx-1" />
        {toolbarBtn('align-left', 'Align Left', () => exec('justifyLeft'))}
        {toolbarBtn('align-center', 'Align Center', () => exec('justifyCenter'))}
        {toolbarBtn('align-right', 'Align Right', () => exec('justifyRight'))}
        {toolbarBtn('align-justify', 'Justify', () => exec('justifyFull'))}
        <div className="w-px h-6 bg-gray-300 mx-1" />
        {toolbarBtn('link', 'Add Link', insertLink)}
        {toolbarBtn('image-line', 'Add Image', insertImage)}
        {toolbarBtn('separator', 'Horizontal Line', () => exec('insertHorizontalRule'))}
        <div className="w-px h-6 bg-gray-300 mx-1" />
        {toolbarBtn('format-clear', 'Clear Format', () => exec('removeFormat'))}
        {toolbarBtn('arrow-go-back-line', 'Undo', () => exec('undo'))}
        {toolbarBtn('arrow-go-forward-line', 'Redo', () => exec('redo'))}
        <div className="w-px h-6 bg-gray-300 mx-1" />
        {/* Font color */}
        <label title="Text Color" className="w-8 h-8 flex items-center justify-center cursor-pointer rounded hover:bg-gray-200">
          <i className="ri-font-color text-gray-700 text-sm"></i>
          <input type="color" className="sr-only" onChange={e => exec('foreColor', e.target.value)} />
        </label>
        {/* Highlight */}
        <label title="Background Color" className="w-8 h-8 flex items-center justify-center cursor-pointer rounded hover:bg-gray-200">
          <i className="ri-mark-pen-line text-gray-700 text-sm"></i>
          <input type="color" className="sr-only" onChange={e => exec('hiliteColor', e.target.value)} />
        </label>
      </div>
      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        className="min-h-[360px] p-4 text-gray-900 bg-white focus:outline-none prose max-w-none
          [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mb-4
          [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mb-3
          [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mb-2
          [&_h4]:text-lg [&_h4]:font-semibold [&_h4]:mb-2
          [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3
          [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3
          [&_blockquote]:border-l-4 [&_blockquote]:border-[#C5A46D] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600
          [&_pre]:bg-gray-100 [&_pre]:p-3 [&_pre]:rounded [&_pre]:font-mono [&_pre]:text-sm
          [&_a]:text-blue-600 [&_a]:underline
          [&_img]:max-w-full [&_img]:rounded [&_img]:my-2
          [&_hr]:border-gray-300 [&_hr]:my-4"
        style={{ minHeight: 360 }}
      />
      {/* Source mode toggle */}
      <div className="border-t border-gray-200 bg-gray-50 px-3 py-1.5 flex justify-end">
        <button
          type="button"
          onClick={() => {
            const src = prompt('Edit HTML source code:', editorRef.current?.innerHTML || '');
            if (src !== null && editorRef.current) {
              editorRef.current.innerHTML = src;
              handleInput();
            }
          }}
          className="text-xs text-gray-500 hover:text-gray-800 cursor-pointer flex items-center gap-1"
        >
          <i className="ri-code-line"></i> HTML Source
        </button>
      </div>
    </div>
  );
};

// ─── Tag Input ────────────────────────────────────────────────────────────────
const TagInput: React.FC<{ tags: string[]; onChange: (tags: string[]) => void }> = ({ tags, onChange }) => {
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setInput('');
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {tags.map(t => (
          <span key={t} className="inline-flex items-center gap-1 px-3 py-1 bg-[#C5A46D]/10 text-[#8B6914] rounded-full text-sm font-medium">
            {t}
            <button type="button" onClick={() => onChange(tags.filter(x => x !== t))} className="hover:text-red-500 cursor-pointer">×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
          placeholder="Type tag and press Enter"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:ring-2 focus:ring-[#C5A46D] focus:border-transparent"
        />
        <button type="button" onClick={add} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm cursor-pointer">Add</button>
      </div>
    </div>
  );
};

// ─── Image Upload Field ───────────────────────────────────────────────────────
const ImageField: React.FC<{
  label: string; value: string;
  onChange: (url: string) => void; hint?: string;
}> = ({ label, value, onChange, hint }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    try {
      setUploading(true);
      const data = await api.upload('/media/upload', file);
      if (!data.success) throw new Error(data.error || 'Upload failed');
      onChange(data.url);
    } catch (e: any) {
      alert(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {hint && <p className="text-xs text-gray-500 mb-2">{hint}</p>}
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Enter URL or upload file"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:ring-2 focus:ring-[#C5A46D] focus:border-transparent"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="px-4 py-2 bg-gray-100 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 text-sm cursor-pointer disabled:opacity-50 flex items-center gap-1"
        >
          <i className="ri-upload-2-line"></i>
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
        />
      </div>
      {value && (
        <div className="relative w-32 h-20 rounded overflow-hidden border border-gray-200">
          <img src={value} alt="preview" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-1 right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs flex items-center justify-center cursor-pointer"
          >×</button>
        </div>
      )}
    </div>
  );
};

// ─── Blog Page SEO ────────────────────────────────────────────────────────────
const BlogPageSEO: React.FC = () => {
  const [form, setForm] = useState({
    title: '', description: '', keywords: '', og_image: '', canonical_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/seo/blog').then(data => {
      if (data) setForm({
        title: data.title || '',
        description: data.description || '',
        keywords: data.keywords || '',
        og_image: data.og_image || '',
        canonical_url: data.canonical_url || '',
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    try {
      setSaving(true);
      await api.post('/seo', { page_key: 'blog', ...form });
      alert('✅ Blog page SEO settings saved successfully!');
    } catch {
      alert('Failed to save SEO settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-gray-500 py-8 text-center">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-blue-800 text-sm flex items-start gap-2">
          <i className="ri-information-line mt-0.5"></i>
          These settings configure the general SEO meta tags for the <strong>/blog</strong> page.
          Individual blog post SEO is configured inside each post editor.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
          <input
            type="text" maxLength={60} value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="Blog | Golden Tower"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white focus:ring-2 focus:ring-[#C5A46D] focus:border-transparent"
          />
          <p className="text-xs text-gray-400 mt-1">{form.title.length}/60 characters</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
          <textarea
            rows={3} maxLength={160} value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            placeholder="Latest news and updates — between 120-160 characters"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white focus:ring-2 focus:ring-[#C5A46D] focus:border-transparent"
          />
          <p className="text-xs text-gray-400 mt-1">{form.description.length}/160 characters</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
          <input
            type="text" value={form.keywords}
            onChange={e => setForm({ ...form, keywords: e.target.value })}
            placeholder="blog, news, golden tower, luxury residence"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white focus:ring-2 focus:ring-[#C5A46D] focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">OG Image URL</label>
          <input
            type="text" value={form.og_image}
            onChange={e => setForm({ ...form, og_image: e.target.value })}
            placeholder="https://..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white focus:ring-2 focus:ring-[#C5A46D] focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Canonical URL</label>
          <input
            type="text" value={form.canonical_url}
            onChange={e => setForm({ ...form, canonical_url: e.target.value })}
            placeholder="https://example.com/blog"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white focus:ring-2 focus:ring-[#C5A46D] focus:border-transparent"
          />
        </div>
      </div>

      {/* Google Preview */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <i className="ri-google-line"></i> Google Search Preview
        </h3>
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="text-blue-600 text-lg mb-0.5 font-medium truncate">{form.title || 'Page Title'}</div>
          <div className="text-green-700 text-sm mb-1.5 truncate">{form.canonical_url || 'https://example.com/blog'}</div>
          <div className="text-gray-600 text-sm line-clamp-2">{form.description || 'Page description will appear here...'}</div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={save} disabled={saving}
          className="px-8 py-2.5 bg-[#C5A46D] hover:bg-[#B8955E] text-white rounded-lg transition-colors cursor-pointer disabled:opacity-50 font-medium"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

// ─── Main BlogManager Component ───────────────────────────────────────────────
export default function BlogManager() {
  const [activeTab, setActiveTab] = useState<Tab>('posts');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'list' | 'form'>('list');
  const [editPost, setEditPost] = useState<Partial<BlogPost> | null>(null);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'draft' | 'published'>('all');
  const [slugManual, setSlugManual] = useState(false);
  const [activeFormTab, setActiveFormTab] = useState<'content' | 'seo' | 'images'>('content');

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get('/blog');
      setPosts(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const openNew = () => {
    setEditPost({ ...EMPTY_FORM });
    setSlugManual(false);
    setActiveFormTab('content');
    setView('form');
  };

  const openEdit = async (post: BlogPost) => {
    try {
      const full = await api.get(`/blog/${post.id}`);
      setEditPost(full);
    } catch {
      setEditPost(post);
    }
    setSlugManual(true);
    setActiveFormTab('content');
    setView('form');
  };

  const handleTitleChange = (title: string) => {
    setEditPost(p => ({
      ...p!,
      title,
      slug: slugManual ? p!.slug : toSlug(title),
    }));
  };

  const handleSave = async () => {
    if (!editPost?.title?.trim()) { alert('Title is required'); return; }
    try {
      setSaving(true);
      if (editPost.id) {
        await api.put(`/blog/${editPost.id}`, editPost);
      } else {
        await api.post('/blog', editPost);
      }
      await fetchPosts();
      setView('list');
    } catch (e: any) {
      alert(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (post: BlogPost) => {
    try {
      await api.post(`/blog/${post.id}/publish`, {});
      await fetchPosts();
    } catch { alert('Failed to publish'); }
  };

  const handleUnpublish = async (post: BlogPost) => {
    try {
      await api.post(`/blog/${post.id}/unpublish`, {});
      await fetchPosts();
    } catch { alert('Failed to unpublish'); }
  };

  const handleDelete = async (post: BlogPost) => {
    if (!confirm(`Are you sure you want to delete "${post.title}"?`)) return;
    try {
      await api.delete(`/blog/${post.id}`);
      await fetchPosts();
    } catch { alert('Failed to delete'); }
  };

  const filtered = posts.filter(p => filterStatus === 'all' || p.status === filterStatus);

  // ── Form Tabs ──────────────────────────────────────────────────────────────
  const formTabBtn = (id: typeof activeFormTab, label: string, icon: string) => (
    <button
      type="button"
      onClick={() => setActiveFormTab(id)}
      className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap
        ${activeFormTab === id ? 'border-[#C5A46D] text-[#C5A46D]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
    >
      <i className={`ri-${icon}`}></i>{label}
    </button>
  );

  // ── Form view ──────────────────────────────────────────────────────────────
  if (view === 'form' && editPost !== null) {
    const p = editPost;
    const set = (key: keyof BlogPost, val: any) => setEditPost(prev => ({ ...prev!, [key]: val }));

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setView('list')}
              className="flex items-center gap-1 text-gray-500 hover:text-gray-800 text-sm cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> Back
            </button>
            <h2 className="text-xl font-bold text-gray-900">
              {p.id ? 'Edit Blog Post' : 'New Blog Post'}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={p.status}
              onChange={e => set('status', e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white cursor-pointer"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-[#C5A46D] hover:bg-[#B8955E] text-white rounded-lg text-sm font-medium transition-colors cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Saving...' : (p.id ? 'Update' : 'Save')}
            </button>
          </div>
        </div>

        {/* Title + Slug */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={p.title || ''}
              onChange={e => handleTitleChange(e.target.value)}
              placeholder="Blog post title"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-lg font-medium text-gray-900 bg-white focus:ring-2 focus:ring-[#C5A46D] focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL)</label>
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#C5A46D]">
              <span className="px-3 py-2 bg-gray-100 text-gray-500 text-sm border-r border-gray-300">/blog/</span>
              <input
                type="text"
                value={p.slug || ''}
                onChange={e => { setSlugManual(true); set('slug', e.target.value); }}
                className="flex-1 px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none"
                placeholder="auto-generated"
              />
            </div>
          </div>
        </div>

        {/* Form sub-tabs */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {formTabBtn('content', 'Content', 'file-text-line')}
            {formTabBtn('images', 'Images', 'image-line')}
            {formTabBtn('seo', 'SEO & Meta', 'search-line')}
          </div>

          <div className="p-6">
            {/* ── CONTENT TAB ─────────────────────────── */}
            {activeFormTab === 'content' && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
                    <input
                      type="text"
                      value={p.author || ''}
                      onChange={e => set('author', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:ring-2 focus:ring-[#C5A46D] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reading Time (min)</label>
                    <input
                      type="number" min={1} max={120}
                      value={p.reading_time || 5}
                      onChange={e => set('reading_time', parseInt(e.target.value) || 5)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:ring-2 focus:ring-[#C5A46D] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
                  <textarea
                    rows={3}
                    value={p.excerpt || ''}
                    onChange={e => set('excerpt', e.target.value)}
                    placeholder="Short summary displayed on blog listings (150-200 characters recommended)"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:ring-2 focus:ring-[#C5A46D] focus:border-transparent"
                  />
                  <p className="text-xs text-gray-400 mt-1">{(p.excerpt || '').length} characters</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                  <TagInput
                    tags={p.tags || []}
                    onChange={tags => set('tags', tags)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                  <HtmlEditor
                    value={p.content || ''}
                    onChange={html => set('content', html)}
                  />
                </div>
              </div>
            )}

            {/* ── IMAGES TAB ──────────────────────────── */}
            {activeFormTab === 'images' && (
              <div className="space-y-8">
                <ImageField
                  label="Featured Image"
                  hint="Thumbnail shown in blog list cards. Recommended: 800×600px"
                  value={p.featured_image || ''}
                  onChange={url => set('featured_image', url)}
                />
                <hr className="border-gray-200" />
                <ImageField
                  label="Cover Image"
                  hint="Hero banner at top of post detail page. Recommended: 1920×600px"
                  value={p.cover_image || ''}
                  onChange={url => set('cover_image', url)}
                />
                <hr className="border-gray-200" />
                <ImageField
                  label="OG Image (Social Media Preview)"
                  hint="Preview image when shared on WhatsApp, Twitter, LinkedIn etc. Recommended: 1200×630px"
                  value={p.og_image || ''}
                  onChange={url => set('og_image', url)}
                />
              </div>
            )}

            {/* ── SEO TAB ─────────────────────────────── */}
            {activeFormTab === 'seo' && (
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SEO Title</label>
                  <input
                    type="text" maxLength={60}
                    value={p.seo_title || ''}
                    onChange={e => set('seo_title', e.target.value)}
                    placeholder="Defaults to post title if left empty"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:ring-2 focus:ring-[#C5A46D] focus:border-transparent"
                  />
                  <p className="text-xs text-gray-400 mt-1">{(p.seo_title || '').length}/60 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SEO Meta Description</label>
                  <textarea
                    rows={3} maxLength={160}
                    value={p.seo_description || ''}
                    onChange={e => set('seo_description', e.target.value)}
                    placeholder="Between 120-160 characters"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white focus:ring-2 focus:ring-[#C5A46D] focus:border-transparent"
                  />
                  <p className="text-xs text-gray-400 mt-1">{(p.seo_description || '').length}/160 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SEO Keywords</label>
                  <input
                    type="text"
                    value={p.seo_keywords || ''}
                    onChange={e => set('seo_keywords', e.target.value)}
                    placeholder="keyword1, keyword2, keyword3"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:ring-2 focus:ring-[#C5A46D] focus:border-transparent"
                  />
                </div>

                {/* Google Preview */}
                <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <p className="text-xs font-medium text-gray-500 mb-3 flex items-center gap-1">
                    <i className="ri-google-line"></i> Google Search Preview
                  </p>
                  <div className="text-blue-600 text-lg mb-0.5 font-medium">
                    {p.seo_title || p.title || 'Post Title'}
                  </div>
                  <div className="text-green-700 text-sm mb-1">
                    goldentower.com/blog/{p.slug || 'post-slug'}
                  </div>
                  <div className="text-gray-600 text-sm">
                    {p.seo_description || p.excerpt || 'Post description will appear here...'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('posts')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer flex items-center gap-2
            ${activeTab === 'posts' ? 'border-[#C5A46D] text-[#C5A46D]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
        >
          <i className="ri-article-line"></i> Blog Posts
        </button>
        <button
          onClick={() => setActiveTab('page-seo')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer flex items-center gap-2
            ${activeTab === 'page-seo' ? 'border-[#C5A46D] text-[#C5A46D]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
        >
          <i className="ri-search-line"></i> /Blog Page SEO
        </button>
      </div>

      {activeTab === 'page-seo' ? (
        <BlogPageSEO />
      ) : (
        <>
          {/* Toolbar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">Blog Posts</h2>
              <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-1 rounded-full">
                {filtered.length} posts
              </span>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value as any)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer"
              >
                <option value="all">All</option>
                <option value="draft">Drafts</option>
                <option value="published">Published</option>
              </select>
              <button
                onClick={openNew}
                className="px-5 py-2 bg-[#C5A46D] hover:bg-[#B8955E] text-white rounded-lg text-sm font-medium transition-colors cursor-pointer flex items-center gap-2"
              >
                <i className="ri-add-line"></i> New Post
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Total', count: posts.length, icon: 'article-line', color: 'bg-gray-100 text-gray-700' },
              { label: 'Published', count: posts.filter(p => p.status === 'published').length, icon: 'checkbox-circle-line', color: 'bg-green-50 text-green-700' },
              { label: 'Draft', count: posts.filter(p => p.status === 'draft').length, icon: 'draft-line', color: 'bg-yellow-50 text-yellow-700' },
            ].map(s => (
              <div key={s.label} className={`${s.color} rounded-lg p-4 flex items-center gap-3`}>
                <i className={`ri-${s.icon} text-2xl`}></i>
                <div>
                  <div className="text-2xl font-bold">{s.count}</div>
                  <div className="text-sm">{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 bg-white border border-dashed border-gray-300 rounded-lg">
              <i className="ri-article-line text-5xl text-gray-300 block mb-3"></i>
              <p className="text-gray-500">No blog posts yet.</p>
              <button
                onClick={openNew}
                className="mt-4 px-5 py-2 bg-[#C5A46D] text-white rounded-lg text-sm font-medium hover:bg-[#B8955E] transition-colors cursor-pointer"
              >
                Add First Post
              </button>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Post</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tags</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filtered.map(post => (
                    <tr key={post.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          {post.featured_image ? (
                            <img src={post.featured_image} alt="" className="w-12 h-9 object-cover rounded border border-gray-200 shrink-0" />
                          ) : (
                            <div className="w-12 h-9 bg-gray-100 rounded border border-gray-200 flex items-center justify-center shrink-0">
                              <i className="ri-image-line text-gray-400"></i>
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 truncate max-w-xs">{post.title}</div>
                            <div className="text-xs text-gray-400 truncate">/blog/{post.slug}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                          ${post.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          <i className={`ri-${post.status === 'published' ? 'checkbox-circle' : 'draft'}-line`}></i>
                          {post.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1">
                          {(post.tags || []).slice(0, 3).map(t => (
                            <span key={t} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{t}</span>
                          ))}
                          {(post.tags || []).length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-400 rounded text-xs">+{post.tags.length - 3}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(post.created_at).toLocaleDateString('en-US')}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {post.status === 'draft' ? (
                            <button
                              onClick={() => handlePublish(post)}
                              title="Publish"
                              className="px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded text-xs hover:bg-green-100 cursor-pointer transition-colors"
                            >
                              Publish
                            </button>
                          ) : (
                            <button
                              onClick={() => handleUnpublish(post)}
                              title="Unpublish"
                              className="px-3 py-1.5 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded text-xs hover:bg-yellow-100 cursor-pointer transition-colors"
                            >
                              Unpublish
                            </button>
                          )}
                          <button
                            onClick={() => openEdit(post)}
                            title="Edit"
                            className="w-8 h-8 flex items-center justify-center bg-gray-100 text-gray-600 rounded hover:bg-gray-200 cursor-pointer transition-colors"
                          >
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(post)}
                            title="Delete"
                            className="w-8 h-8 flex items-center justify-center bg-red-50 text-red-500 rounded hover:bg-red-100 cursor-pointer transition-colors"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
